import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { BrandOrbitMark } from './BrandMark.js';
import { claimOnBrandSystemBars, hideNativeSplash } from '../lib/nativeSplash.js';
import { useSession } from '../session/SessionProvider.js';

/** Shown under the wordmark. A constant so marketing can change the line without reading the lifecycle. */
export const LAUNCH_TAGLINE = 'HR that works for everyone';

/**
 * The mark's size in CSS px (one px is one dp in an Android WebView). It must equal the drawing in
 * native-android/res/drawable/splash_icon.xml, 120dp (the 40-unit mark scaled x3), or the logo
 * visibly jumps as the OS splash fades off it. styles.css's `--launch-mark` is the same number, for
 * the centring maths; change all three together.
 */
export const LAUNCH_MARK_PX = 120;

/**
 * Timings, in ms. The minimum is long enough for the entrance animation to read as deliberate
 * rather than as a flicker; the cap is what a slow `/me` costs at worst — after it the splash
 * leaves and the app's own loading state takes over. Reduced motion has nothing to watch, so it
 * only waits long enough not to flash.
 */
export const LAUNCH_SPLASH_TIMING = {
  minVisible: 1200,
  minVisibleReduced: 400,
  maxVisible: 3000,
  fadeOut: 300,
  fadeOutReduced: 120,
} as const;

/**
 * Once per cold start, not once per mount: a WebView reload is a cold start (fresh module), but a
 * remount from an error boundary or a hot update is not, and must not replay the brand moment.
 */
let shownThisLaunch = false;

/** Test seam: a fresh module state per test without `vi.resetModules()`. */
export function resetLaunchSplashForTests() {
  shownThisLaunch = false;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

type Phase = 'shown' | 'leaving' | 'gone';

/**
 * The phone app's launch screen. The OS splash (Android's SplashScreen theme) can only show a
 * static icon on a flat colour and cannot know when the app is ready; this takes over from it on
 * the first frame — the same still mark on the same flat `--launch-mid` ground, so the hand-off is
 * invisible — then lets the gradient, glow and colour fields fade in and sends the satellite round
 * its orbit, and leaves once the session has resolved, revealing login or home already rendered
 * underneath.
 *
 * It fills the viewport, and asks for launch-blue system bars with light icons while it is up
 * (lib/nativeSplash.ts).
 *
 * It is an overlay above `<App />`, never a gate in front of it: the session query, the route's
 * lazy chunk and the shell all start in parallel with the animation, so the splash costs no time
 * the app was not already spending. It does not take focus or trap it, and it stops swallowing
 * touches the moment it starts to fade.
 *
 * Web never sees it — a browser has no OS splash to hand off from, and a brand screen in front of
 * a website is just latency.
 */
export function LaunchSplash() {
  // Decided once, at first render: whether this mount is the one that owns the launch.
  const [active] = useState(() => !shownThisLaunch && Capacitor.isNativePlatform());
  const [reduced] = useState(prefersReducedMotion);
  const [phase, setPhase] = useState<Phase>(active ? 'shown' : 'gone');
  const [minElapsed, setMinElapsed] = useState(false);
  const { status } = useSession();

  useEffect(() => {
    if (!active) return;
    shownThisLaunch = true;
    // Straight away, not after a frame: this screen cannot paint first. While the OS splash is up the
    // plugin holds a pre-draw listener on the activity's content that refuses every draw, so the
    // WebView draws nothing until hide() is called, and a requestAnimationFrame can stall waiting on
    // that same blocked draw. By now this screen is in the DOM, so the first frame drawn once the
    // block lifts is this one, and it is what shows through the OS splash's fade.
    void hideNativeSplash();
    const min = setTimeout(
      () => setMinElapsed(true),
      reduced ? LAUNCH_SPLASH_TIMING.minVisibleReduced : LAUNCH_SPLASH_TIMING.minVisible,
    );
    const max = setTimeout(
      () => setPhase((p) => (p === 'shown' ? 'leaving' : p)),
      LAUNCH_SPLASH_TIMING.maxVisible,
    );
    return () => {
      clearTimeout(min);
      clearTimeout(max);
    };
  }, [active, reduced]);

  // Light status/navigation-bar icons on the blue while it shows; released as it goes.
  const visible = active && phase !== 'gone';
  useEffect(() => (visible ? claimOnBrandSystemBars('all') : undefined), [visible]);

  // 'error' and 'anonymous' are resolved too — the app underneath has something to show for both.
  useEffect(() => {
    if (phase === 'shown' && minElapsed && status !== 'loading') setPhase('leaving');
  }, [phase, minElapsed, status]);

  useEffect(() => {
    if (phase !== 'leaving') return;
    const t = setTimeout(
      () => setPhase('gone'),
      reduced ? LAUNCH_SPLASH_TIMING.fadeOutReduced : LAUNCH_SPLASH_TIMING.fadeOut,
    );
    return () => clearTimeout(t);
  }, [phase, reduced]);

  if (phase === 'gone') return null;

  return (
    <div
      role="status"
      aria-label="PeopleOrbit is starting"
      aria-busy={phase === 'shown'}
      data-phase={phase}
      data-reduced-motion={reduced || undefined}
      className="launch-splash"
    >
      <div className="launch-splash-backdrop" aria-hidden="true">
        <span className="launch-splash-glow" />
        <span className="launch-splash-tint launch-splash-tint-cool" />
        <span className="launch-splash-tint launch-splash-tint-warm" />
      </div>
      <div className="launch-splash-mark">
        <BrandOrbitMark size={LAUNCH_MARK_PX} animated={!reduced} />
      </div>
      <div className="launch-splash-copy" aria-hidden="true">
        <p className="launch-splash-wordmark">PeopleOrbit</p>
        <p className="launch-splash-tagline">{LAUNCH_TAGLINE}</p>
      </div>
    </div>
  );
}
